import React, { useEffect, useState } from "react";
import { getAllAdmins, createAdmin, updateAdmin, deleteAdmin } from "../../API/adminDetails";
import MainTable from "../../components/Datagrid/dataGridTable";
import AddAdmin from "../../components/addNewAdmin/addAdmin";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { SUCCESS_TOAST, ERROR_TOAST } from "../../helpers/toasters";

interface Admin {
    _id: string;
    name: string;
    email: string;
    isSuper: boolean;
    isContentAdmin: boolean;
}

export default function ManageAdmins() {
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState<string | null>(null);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const data = await getAllAdmins();
            setAdmins(data);
        } catch (error) {
            console.error(error);
            ERROR_TOAST("Failed to fetch admins");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAdmin = async (adminData: any) => {
        try {
            if (isEditMode && selectedAdmin) {
                const { password, ...updatedData } = adminData;
                await updateAdmin(selectedAdmin._id, updatedData);
                SUCCESS_TOAST("Admin updated successfully!");
            } else {
                await createAdmin(adminData);
                SUCCESS_TOAST("Admin created successfully!");
            }

            fetchAdmins();
            setModalOpen(false);
            setSelectedAdmin(null);
        } catch (error) {
            console.error("Error in handleSaveAdmin:", error);
            ERROR_TOAST("Failed to save admin");
        }
    };

    const handleDeleteAdmin = async () => {
        if (!adminToDelete) return;

        try {
            await deleteAdmin(adminToDelete);
            SUCCESS_TOAST("Admin deleted successfully!");
            fetchAdmins();
        } catch (error) {
            console.error("Error in handleDeleteAdmin:", error);
            ERROR_TOAST("Failed to delete admin");
        } finally {
            setDeleteConfirmOpen(false);
            setAdminToDelete(null);
        }
    };

    const columns = [
        { field: "name", headerName: "Name", flex: 1 },
        { field: "email", headerName: "Email", flex: 1 },
        {
            field: "isSuper",
            headerName: "Super Admin",
            flex: 1,
            renderCell: (params: any) => (params.value ? "Yes" : "No"),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params: any) => (
                <>
                    <Button
                        size="small"
                        onClick={() => {
                            setSelectedAdmin(params.row);
                            setIsEditMode(true);
                            setModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => {
                            setAdminToDelete(params.row._id);
                            setDeleteConfirmOpen(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h2>Manage Admins</h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedAdmin(null);
                        setIsEditMode(false);
                        setModalOpen(true);
                    }}
                >
                    Add Admin
                </Button>
            </div>

            <h3>Admin List</h3>
            <MainTable rows={admins} columns={columns} loading={loading} />

            <AddAdmin
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveAdmin}
                initialData={selectedAdmin || { name: "", email: "", password: "", isSuper: false, isContentAdmin: false }}
                isEdit={isEditMode}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this admin? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteAdmin} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
